import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { login as apiLogin, clearTokens, loadTokens, whoami, type Identity } from "./api";

interface AuthState {
  authenticated: boolean;
  identity: Identity | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // On mount, if a token exists verify it server-side via /whoami.
  useEffect(() => {
    (async () => {
      if (loadTokens()) {
        const me = await whoami();
        if (me) {
          setIdentity(me);
          setAuthenticated(true);
        } else {
          clearTokens();
        }
      }
      setLoading(false);
    })();
  }, []);

  const signIn = async (email: string, password: string) => {
    await apiLogin(email, password);
    const me = await whoami(); // confirm the session server-side
    setIdentity(me);
    setAuthenticated(true);
  };
  const signOut = () => {
    clearTokens();
    setIdentity(null);
    setAuthenticated(false);
  };
  return <Ctx.Provider value={{ authenticated, identity, loading, signIn, signOut }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
